import React, { useState } from 'react';
import { User } from '@models/user';
import { Alert, App, Button, Card, Form, Image, Input, Row, Col, Divider } from 'antd';
import { LockOutlined, UserOutlined } from '@ant-design/icons';
import { setUser } from '../../redux/userSlicer';
import { useDispatch } from 'react-redux';
import {  useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import srcImg from '../assets/images/blue-outline.svg'
import srcBcg from '../assets/images/login-background.png'
import { GoogleLogin } from '@react-oauth/google';
import { makeRequest } from '@helpers/axios-manipulator';
import {jwtDecode} from 'jwt-decode';

type loginProps = {
  onLogin: (userData: User) => void;
};

// Interface for decoded token
interface DecodedToken {
  aud: string;
  azp: string;
  email: string;
  email_verified: boolean;
  exp: number;
  family_name: string;
  given_name: string;
  hd?: string;
  iat: number;
  iss: string;
  jti: string;
  name: string;
  nbf: number;
  sub: string;
};


const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

const Login: React.FC<loginProps> = ({ onLogin }) => {
  const isSmallScreen = window.innerWidth < 500;
  const { message } = App.useApp();
  const [notice, setNotice] = useState('');
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Validate the token
  const validateToken = (decodedToken: DecodedToken) => {
    const expectedAudience = GOOGLE_CLIENT_ID;
    const expectedIssuer = "https://accounts.google.com";

    // Check token validity
    if (decodedToken.aud !== expectedAudience) {
      return false;
    }

    if (decodedToken.iss !== expectedIssuer) {
      return false;
    }

    if (!decodedToken.email_verified) {
      return false;
    }

    if (decodedToken.exp < Date.now() / 1000) {
      return false;
    }

    return true;
  };

  const handleSuccess = async (credentialResponse: any) => {
  try {
    const token = credentialResponse.credential;
    const decodedToken: DecodedToken = jwtDecode(token);

    // Validate the token
    if (!validateToken(decodedToken)) {
      message.error("Error validating Google token.");
      return;
    }

    // Send the Google credential token to backend for verification
    const loginResponse = await makeRequest({
      origin: window.location.origin,
      method: 'post',
      url: '/api-google-auth',
      headers: {},
      data: { token },
    });

    if (!loginResponse || !loginResponse.user) {
      throw new Error('Invalid authentication response');
    }

    // Save JWT token to localStorage for authenticated API requests
    if (loginResponse.token) {
      localStorage.setItem('jwt', loginResponse.token);
    }

    // Normalize user object to ensure id, rep_id, and google_id fields are set
    const userFromBackend = loginResponse.user;
    const normalizedUser = {
      ...userFromBackend,
      id: userFromBackend.id || userFromBackend.userId || undefined,
      rep_id: userFromBackend.rep_id || userFromBackend.business_id || userFromBackend.repId || undefined,
      google_id: userFromBackend.google_id || userFromBackend.googleId || userFromBackend.sub || decodedToken.sub || undefined,
    };
    Cookies.set('user', JSON.stringify(normalizedUser));
    dispatch(setUser(normalizedUser));
    message.success('Successfully authenticated with Google!');
    // Always redirect root to dashboard
    if (normalizedUser.contract_id === 0 || normalizedUser.contract_id === '0' || normalizedUser.profile_name === 'Root') {
      navigate('/');
    } else {
      navigate('/placards');
    }
  } catch (error) {
    if (error && typeof error === 'object' && 'message' in error) {
      message.error((error as Error).message, 5);
    } else {
      message.error("Error logging in to Business Admin.");
    }
  }
};

  const handleError = () => {
    message.error("Failed to log in with Google.");
  };


  const onFinish = async (values: any) => {
    try {
      // Send credentials to backend for authentication
      const loginResponse = await makeRequest({
        origin: window.location.origin,
        method: 'post',
        url: '/api-admin-auth',
        headers: { 'Content-Type': 'application/json' },
        data: {
          username: values.username,
          password: values.password
        },
      });

      if (!loginResponse || !loginResponse.user) {
        throw new Error('Invalid authentication response');
      }

      // Save JWT token to localStorage for authenticated API requests
      if (loginResponse.token) {
        localStorage.setItem('jwt', loginResponse.token);
      }

      // Save user to cookies and Redux
      const userData = loginResponse.user;
      Cookies.set('user', JSON.stringify(userData));
      dispatch(setUser(userData));

      message.success('User authenticated with success!');
      navigate('/');
    } catch (error: any) {
      // Show error message after a small delay for better UX
      setTimeout(() => {
        if (error?.response?.status === 401) {
          message.error('The combination of username and password is incorrect!', 5);
        } else if (error?.message) {
          message.error(error.message, 5);
        } else {
          message.error('Error logging in. Please try again.', 5);
        }
      }, 500);
    }
  };

  return (
    <div style={{height: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    backgroundImage: `url(${srcBcg})`, backgroundRepeat: 'no-repeat', backgroundSize: 'cover'}}>
      <Card style={{ width: 350, height: 'fit-content', background: '#15013a',  border: 'none' }}>
        <Image preview={false} width={isSmallScreen? 300: 300} src={srcImg} />
        <Divider style={{width: "100%", borderTop: '1px solid gold'}} />
          {notice && <Alert style={{ marginBottom: 24 }} title={notice} type="error" showIcon closable />}
          <Form onFinish={onFinish}>
            <Form.Item name="username" rules={[{ required: true, message: 'Please input your username!' }]}>
              <Input prefix={<UserOutlined />} placeholder="Username" autoComplete="username" />
            </Form.Item>
            <Form.Item name="password" rules={[{ required: true, message: 'Please input your password!' }]}>
              <Input.Password prefix={<LockOutlined />} placeholder="Password" autoComplete="current-password" />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" style={{ color: '#15013a', background: 'rgb(247 229 185)', width: '100%'}}>
                Login
              </Button>
            </Form.Item>
          </Form>

          <Divider style={{width: "100%", borderTop: '1px solid white'}} />

        <div style={{width: "100%", display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center"}}>
      
        <GoogleLogin
          onSuccess={handleSuccess}
          onError={handleError}
        />
      </div>
      </Card>
    </div>
  );
};;

export default Login;
